import {
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import AutoSuggestWidget from 'react-autosuggest';
import debounce from 'lodash/debounce';

import * as Styled from './lesson-materials-item-edit.style';
import DropdownSingleSelect from 'approot/shared/dropdown/dropdown-single-select';
import {
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_GROUP,
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT,
  LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_CLASS,
  LessonMaterialGroupTypes,
} from 'global/constants';
import {
  FormData,
  MIN_NUMBER,
  MAX_NUMBER_IN_GROUP,
  MAX_NUMBER_OF_ITEMS,
  NUMBER_IN_GROUP_FIELD,
  NUMBER_OF_ITEMS_FIELD,
  ERROR_NUMBER_OF_ITEMS_ID,
  ERROR_NUMBER_IN_GROUP_ID,
  MATERIAL_FIELD,
  OPTIONAL_MATERIAL_FIELD,
  NOTE_FIELD,
  ALTERNATE_MATERIAL_FIELD,
  GROUP_TYPE_FIELD,
  MaterialItem,
  UNIT_OF_MEASUREMENT_FIELD,
} from './lesson-materials-item-edit.constants';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { Box } from 'approot/shared/design-system/box/box.style';
import { validateNumberInput } from 'approot/shared/logic/number-input-validation';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { SEARCH_MATERIALS } from './lesson-materials.graphql';
import { theme as defaultTheme } from 'approot/shared/autosuggest/autosuggest.config';
import type {
  SearchMaterialsQuery,
  SearchMaterialsQueryVariables,
} from './lesson-materials.graphql.generated';
import CheckboxInput from 'lib/form/checkbox-input/checkbox-input';
import { ENTER_KEY } from 'lib/strings/strings';
import { SettingsLabel } from 'approot/activity-builder/settings/settings.style';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import IconClose from 'approot/shared/icons/icon-close';
import { NumberInputStyled } from 'approot/shared/styles/inputs.style';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button.style';
import LessonMaterialsList from 'approot/shared/lesson-materials/lesson-materials-list';
import { filterOutSelectedMaterials } from './lesson-materials-edit.utils';
import type { LessonMaterialFragment } from '../../../../shared/lesson-materials/lesson-material-fragment.generated';
import { Dropdown } from 'approot/shared/dropdown/dropdown';
import { getMaterialUnitOfMeasurementData } from './units-of-measurement/units-of-measurement-utils';
import type { Pluck } from 'lib/gql/pluck';

type BaseProps = {
  material: MaterialItem;
  onDeleteItem?: () => void;
  onSave: (material: MaterialItem) => void;
  isLegacyMaterial: boolean;
  existingMaterials: MaterialItem[];
};

type RenderSuggestionsContainerProps = {
  containerProps: HTMLAttributes<HTMLDivElement>;
  children: ReactNode;
  query: string;
  isLoading: boolean;
};

const RenderSuggestionsContainer = ({
  containerProps,
  children,
  query,
  isLoading,
}: RenderSuggestionsContainerProps) => (
  <div {...containerProps}>
    {isLoading && query ? (
      <Styled.LessonMaterialSearchInfo>
        {`Searching for ${query}...`}
      </Styled.LessonMaterialSearchInfo>
    ) : (
      children
    )}
  </div>
);

const LessonMaterialsItemEdit = ({
  material,
  onDeleteItem,
  onSave,
  isLegacyMaterial,
  existingMaterials,
}: BaseProps) => {
  const [materialName, setMaterialName] = useState<string>('');
  const [hasAlternateMaterial, setHasAlternateMaterial] = useState<boolean>(
    !!(
      material.alternativeMaterials && material.alternativeMaterials.length > 0
    )
  );
  const [selectedMaterial, setSelectedMaterial] = useState<
    Pluck<SearchMaterialsQuery, 'searchMaterials'> | MaterialItem | null
  >(!isLegacyMaterial ? material : null);

  const [alternativeMaterials, setAlternativeMaterials] = useState<
    Pluck<LessonMaterialFragment, 'alternativeMaterials'>[]
  >(material.alternativeMaterials || []);

  const [hasNote, setHasNote] = useState<boolean>(!!material.note);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [GROUP_TYPE_FIELD]: material.groupType,
      [NUMBER_IN_GROUP_FIELD]: material.numberInGroup,
      [NUMBER_OF_ITEMS_FIELD]: material.numberOfItems || 1,
      [MATERIAL_FIELD]: material.materialId,
      [OPTIONAL_MATERIAL_FIELD]: !!material.isOptional,
      [ALTERNATE_MATERIAL_FIELD]: material.alternativeMaterials?.map(
        am => am.id
      ),
      [NOTE_FIELD]: material.note,
      [UNIT_OF_MEASUREMENT_FIELD]: material.unitOfMeasurementId,
    },
  });

  const {
    register,
    formState: { errors },
    watch,
    getValues,
    setValue,
    reset,
    handleSubmit,
    setError,
    clearErrors,
  } = formMethods;

  const [materialField, groupTypeField, numberOfItems, unitOfMeasurementField] =
    watch([
      MATERIAL_FIELD,
      GROUP_TYPE_FIELD,
      NUMBER_OF_ITEMS_FIELD,
      UNIT_OF_MEASUREMENT_FIELD,
    ]);

  const [searchMaterials, { data: searchMaterialsList }] = useLoggingLazyQuery<
    SearchMaterialsQuery,
    SearchMaterialsQueryVariables
  >(SEARCH_MATERIALS, {
    skip: !materialName,
    onCompleted: () => {
      setIsLoading(false);
      clearErrors(ALTERNATE_MATERIAL_FIELD);
    },
    onError: () => {
      setIsLoading(false);
      setError(MATERIAL_FIELD, {
        message: 'Uh-oh! Please check your internet and try again.',
        type: 'custom',
      });
    },
  });

  const debouncedSearch = useMemo(
    () => debounce(searchMaterials, 300),
    [searchMaterials]
  );

  const handleOnChange = useCallback(
    (params: any) => {
      if (params.method === 'type') {
        setMaterialName(params.newValue);
        // set a loading state for the debounce delay
        setIsLoading(true);
        debouncedSearch({
          variables: {
            input: {
              materialName: params.newValue,
              includeUnits: true,
            },
          },
        });
        if (!params.newValue || params.newValue.trim() === '') {
          setMaterialName('');
        }
      }
    },
    [debouncedSearch]
  );

  const materialsList = useMemo(
    () =>
      searchMaterialsList?.searchMaterials?.filter(
        material => !existingMaterials.find(m => m.materialId === material.id)
      ),
    [existingMaterials, searchMaterialsList?.searchMaterials]
  );

  const {
    hasUnitOfMeasurementOptions,
    materialUnitOfMeasurementOptions,
    selectedUnitOfMeasurementLabel,
  } = useMemo(
    () =>
      getMaterialUnitOfMeasurementData({
        material: selectedMaterial,
        numberOfItems,
        selectedUnitOfMeasurementId: unitOfMeasurementField,
      }),
    [numberOfItems, selectedMaterial, unitOfMeasurementField]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      const materialId = data[MATERIAL_FIELD];

      onSave({
        __typename: 'LessonMaterial',
        id: material.id,
        isEditMode: false,
        alternativeMaterials:
          alternativeMaterials?.map(am => {
            return {
              __typename: 'Material',
              id: am.id,
              name: am.name,
            };
          }) || [],
        groupType: data[GROUP_TYPE_FIELD] as LessonMaterialGroupTypes,
        isOptional: data[OPTIONAL_MATERIAL_FIELD],
        materialId,
        name: selectedMaterial?.name || '',
        note: data[NOTE_FIELD] || null,
        numberInGroup: Number(data[NUMBER_IN_GROUP_FIELD]) || null,
        numberOfItems: Number(data[NUMBER_OF_ITEMS_FIELD]),
        normalisedNumberOfItems: null,
        isReusable: selectedMaterial?.isReusable || null,
        isStandardised: selectedMaterial?.isStandardised || null,
        unitOfMeasurementId: unitOfMeasurementField || null,
        unitsOfMeasurement: selectedMaterial?.unitsOfMeasurement || [],
      });
    },
    [
      alternativeMaterials,
      material.id,
      onSave,
      selectedMaterial?.isReusable,
      selectedMaterial?.isStandardised,
      selectedMaterial?.name,
      selectedMaterial?.unitsOfMeasurement,
      unitOfMeasurementField,
    ]
  );

  useEffect(() => {
    register(MATERIAL_FIELD, {
      required: 'Psst, you need to add a material',
    });
    register(GROUP_TYPE_FIELD, {
      required: true,
    });
    register(UNIT_OF_MEASUREMENT_FIELD);
  }, [register]);

  return (
    <FormProvider {...formMethods}>
      <Styled.LessonMaterialsItem>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Styled.LessonMaterialsItemInner>
            <SettingsLabel htmlFor={MATERIAL_FIELD}>Material</SettingsLabel>
            <Styled.LessonMaterialsRow $alignItems="flex-start">
              <Styled.LessonMaterialsAutoSuggest
                $itemClassName="item"
                data-selector="activity-material-name-edit"
              >
                <AutoSuggestWidget<
                  Pluck<SearchMaterialsQuery, 'searchMaterials'>,
                  Pluck<SearchMaterialsQuery, 'searchMaterials'>
                >
                  getSuggestionValue={suggestion => suggestion.name}
                  inputProps={{
                    id: MATERIAL_FIELD,
                    name: MATERIAL_FIELD,
                    type: 'search',
                    disabled: !!materialField,
                    autoComplete: 'off',
                    onChange: (_, params) => {
                      handleOnChange(params);
                    },
                    onKeyDown: event => {
                      if (event.key === ENTER_KEY) {
                        event.preventDefault();
                      }
                    },
                    value: materialName || '',
                    placeholder: 'Material',
                  }}
                  onSuggestionsFetchRequested={event =>
                    setMaterialName(event.value)
                  }
                  onSuggestionsClearRequested={() => {}}
                  onSuggestionSelected={(_, data) => {
                    setSelectedMaterial(data.suggestion);
                    setMaterialName('');
                    setValue(MATERIAL_FIELD, data.suggestion.id, {
                      shouldValidate: true,
                    });
                    setValue(
                      UNIT_OF_MEASUREMENT_FIELD,
                      data.suggestion.unitsOfMeasurement?.[0]?.id
                    );
                  }}
                  renderSuggestion={suggestion => {
                    return (
                      <div
                        key={suggestion.id}
                        data-selector="activity-material-suggestion-option"
                      >
                        {suggestion.name}
                      </div>
                    );
                  }}
                  suggestions={materialsList || []}
                  theme={defaultTheme}
                  renderSuggestionsContainer={options => (
                    <RenderSuggestionsContainer
                      {...options}
                      isLoading={isLoading}
                    />
                  )}
                />
                <ValidationMessage
                  noMarginTop
                  id={MATERIAL_FIELD}
                  field={MATERIAL_FIELD}
                  errors={errors}
                  data-selector="activity-material-field-validation-message"
                />
              </Styled.LessonMaterialsAutoSuggest>
            </Styled.LessonMaterialsRow>
            {selectedMaterial?.name && (
              <Box $marginTop={8}>
                <Styled.LessonMaterialsPill
                  type="button"
                  $isSmall
                  $withIcon
                  $borderRadius={40}
                  aria-label={`Click to remove ${selectedMaterial.name}`}
                  onClick={() => {
                    setValue(MATERIAL_FIELD, null);
                    setValue(UNIT_OF_MEASUREMENT_FIELD, null);
                    setSelectedMaterial(null);
                  }}
                  data-selector="remove-activity-material"
                >
                  {selectedMaterial.name}
                  <IconClose width={12} height={12} />
                </Styled.LessonMaterialsPill>
              </Box>
            )}
          </Styled.LessonMaterialsItemInner>

          <Styled.LessonMaterialsItemInner>
            <SettingsLabel htmlFor={NUMBER_OF_ITEMS_FIELD}>
              Quantity
            </SettingsLabel>
            <Styled.LessonMaterialsRow>
              <NumberInputStyled
                id={NUMBER_OF_ITEMS_FIELD}
                {...register(NUMBER_OF_ITEMS_FIELD, {
                  validate: value =>
                    validateNumberInput({
                      value,
                      min: MIN_NUMBER,
                      max: MAX_NUMBER_OF_ITEMS,
                      fieldName: 'Quantity',
                    }),
                })}
                min={MIN_NUMBER}
                max={MAX_NUMBER_OF_ITEMS}
                $width={88}
                aria-label="Enter the quantity for this lesson material"
                aria-invalid={!!errors?.[NUMBER_OF_ITEMS_FIELD]}
                aria-describedby={ERROR_NUMBER_OF_ITEMS_ID}
                data-selector="activity-material-number-edit"
                $hasError={!!errors?.[NUMBER_OF_ITEMS_FIELD]}
              />
              {hasUnitOfMeasurementOptions && (
                <Dropdown
                  options={materialUnitOfMeasurementOptions}
                  onSelect={option => {
                    setValue(UNIT_OF_MEASUREMENT_FIELD, Number(option.value));
                  }}
                  selectedValue={selectedUnitOfMeasurementLabel}
                  menuWidth={140}
                  aria-label="Select the unit of measurement"
                />
              )}
            </Styled.LessonMaterialsRow>
            <ValidationMessage
              id={ERROR_NUMBER_OF_ITEMS_ID}
              field={NUMBER_OF_ITEMS_FIELD}
              errors={errors}
              noMarginTop
              data-selector="activity-material-quantity-validation-message"
            />
          </Styled.LessonMaterialsItemInner>

          <Styled.LessonMaterialsItemInner>
            <Styled.LessonMaterialsRow>
              <Styled.LessonMaterialsGroupLabel>
                per
              </Styled.LessonMaterialsGroupLabel>

              <Box>
                <DropdownSingleSelect
                  data-selector="lesson-material-group"
                  options={[
                    {
                      label: 'Student',
                      value:
                        LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT,
                    },
                    {
                      label: 'Group',
                      value: LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_GROUP,
                    },
                    {
                      label: 'Class',
                      value: LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_CLASS,
                    },
                  ]}
                  selectedDropdownOptions={[groupTypeField]}
                  aria-label="Select the lesson materials group type"
                  handleChange={value => {
                    reset({
                      ...getValues(),
                      [NUMBER_IN_GROUP_FIELD]:
                        value ===
                        LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_STUDENT
                          ? null
                          : 1,
                      [GROUP_TYPE_FIELD]: value,
                    });
                  }}
                />
              </Box>

              {groupTypeField ===
                LEARNING_SEQUENCE_LESSON_MATERIAL_GROUP_TYPE_GROUP && (
                <>
                  <Styled.LessonMaterialsGroupLabel>
                    of
                  </Styled.LessonMaterialsGroupLabel>

                  <NumberInputStyled
                    id={NUMBER_IN_GROUP_FIELD}
                    {...register(NUMBER_IN_GROUP_FIELD, {
                      validate: value =>
                        validateNumberInput({
                          value: value?.toString() || '',
                          min: MIN_NUMBER,
                          max: MAX_NUMBER_IN_GROUP,
                          fieldName: 'Number in group',
                        }),
                    })}
                    min={MIN_NUMBER}
                    max={MAX_NUMBER_IN_GROUP}
                    $width={70}
                    aria-label="Enter the number of students in a group for this lesson material item"
                    aria-invalid={!!errors?.[NUMBER_IN_GROUP_FIELD]}
                    aria-describedby={ERROR_NUMBER_IN_GROUP_ID}
                    $hasError={!!errors?.[NUMBER_IN_GROUP_FIELD]}
                  />
                </>
              )}
            </Styled.LessonMaterialsRow>
            <ValidationMessage
              id={ERROR_NUMBER_IN_GROUP_ID}
              field={NUMBER_IN_GROUP_FIELD}
              errors={errors}
              noMarginTop
            />
          </Styled.LessonMaterialsItemInner>

          <Styled.LessonMaterialsItemInner>
            <Styled.LessonMaterialsRow>
              <CheckboxInput
                id={OPTIONAL_MATERIAL_FIELD}
                {...register(OPTIONAL_MATERIAL_FIELD)}
                label="Optional"
                data-selector="activity-material-optional-checkbox"
              />
            </Styled.LessonMaterialsRow>
          </Styled.LessonMaterialsItemInner>

          <Styled.LessonMaterialsItemInner>
            <Styled.LessonMaterialsRow>
              <SecondaryButton
                type="button"
                disabled={hasNote}
                $isSmall
                onClick={() => setHasNote(true)}
                data-selector="add-notes-button"
              >
                Add Notes
              </SecondaryButton>
              <SecondaryButton
                type="button"
                $isSmall
                disabled={hasAlternateMaterial}
                onClick={() => setHasAlternateMaterial(true)}
                data-selector="add-alternative-materials-button"
              >
                Add Alt. Materials
              </SecondaryButton>
            </Styled.LessonMaterialsRow>
          </Styled.LessonMaterialsItemInner>

          {hasAlternateMaterial && (
            <Styled.LessonMaterialsItemInner>
              <AlternativeMaterialsField
                alternativeMaterials={alternativeMaterials}
                setAlternativeMaterials={setAlternativeMaterials}
                onRemoveAll={() => setHasAlternateMaterial(false)}
              />
            </Styled.LessonMaterialsItemInner>
          )}

          {hasNote && (
            <Styled.LessonMaterialsItemInner>
              <NotesField onDelete={() => setHasNote(false)} />
            </Styled.LessonMaterialsItemInner>
          )}

          <Box $justifyContent="space-between" $alignItems="center">
            <PrimaryButton type="submit">Save</PrimaryButton>
            {onDeleteItem && (
              <Styled.LessonMaterialsItemInner>
                <LinkButton
                  type="button"
                  $error
                  data-selector="delete-activity-material-button"
                  aria-label={`Remove ${
                    material.name?.length ? material.name : 'this'
                  } lesson material item`}
                  onClick={onDeleteItem}
                >
                  Delete Material
                </LinkButton>
              </Styled.LessonMaterialsItemInner>
            )}
          </Box>
        </form>
      </Styled.LessonMaterialsItem>
    </FormProvider>
  );
};

const NotesField = ({ onDelete }: { onDelete: () => void }) => {
  const { watch, register, setValue } = useFormContext<FormData>();
  const note = watch(NOTE_FIELD);

  return (
    <>
      <SettingsLabel htmlFor="notes">Notes</SettingsLabel>
      <Styled.LessonMaterialsRow $alignItems="flex-start">
        <Styled.LessonMaterialsTextAreaWrapper>
          <AutoResizingTextArea
            id={NOTE_FIELD}
            maxLength={50}
            {...register(NOTE_FIELD)}
            showCounter
            initialValue={note || ''}
            onChange={e => setValue(NOTE_FIELD, e.target.value)}
            data-selector="activity-material-notes"
          />
        </Styled.LessonMaterialsTextAreaWrapper>
        <DeleteButtonIcon
          aria-label="Delete notes"
          onClick={() => {
            setValue(NOTE_FIELD, null);
            onDelete();
          }}
          data-selector="delete-notes-button"
        />
      </Styled.LessonMaterialsRow>
    </>
  );
};

const AlternativeMaterialsField = ({
  alternativeMaterials,
  setAlternativeMaterials,
  onRemoveAll,
}: {
  alternativeMaterials: Pluck<LessonMaterialFragment, 'alternativeMaterials'>[];
  setAlternativeMaterials: (
    materials: Pluck<LessonMaterialFragment, 'alternativeMaterials'>[]
  ) => void;
  onRemoveAll: () => void;
}) => {
  const [materialName, setMaterialName] = useState<string>('');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    register,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
  } = useFormContext<FormData>();

  const [searchMaterials, { data: searchMaterialsList }] = useLoggingLazyQuery<
    SearchMaterialsQuery,
    SearchMaterialsQueryVariables
  >(SEARCH_MATERIALS, {
    skip: !materialName,
    onCompleted: () => {
      setIsLoading(false);
      clearErrors(ALTERNATE_MATERIAL_FIELD);
    },
    onError: () => {
      setIsLoading(false);
      setError(ALTERNATE_MATERIAL_FIELD, {
        message: 'Uh-oh! Please check your internet and try again.',
        type: 'custom',
      });
    },
  });

  const debouncedSearch = useMemo(
    () => debounce(searchMaterials, 300),
    [searchMaterials]
  );

  const handleOnChange = useCallback(
    (params: any) => {
      if (params.method === 'type') {
        setMaterialName(params.newValue);
        // set a loading state for the debounce delay
        setIsLoading(true);
        debouncedSearch({
          variables: {
            input: {
              materialName: params.newValue,
            },
          },
        });
        if (!params.newValue || params.newValue.trim() === '') {
          setMaterialName('');
        }
      }
    },
    [debouncedSearch]
  );

  const materialsList = searchMaterialsList?.searchMaterials;

  const alternativeMaterialIds = useMemo(
    () => alternativeMaterials.map(am => am.id),
    [alternativeMaterials]
  );

  const filteredList = useMemo(
    () =>
      filterOutSelectedMaterials(materialsList || [], alternativeMaterialIds),
    [alternativeMaterialIds, materialsList]
  );

  useEffect(() => {
    register(ALTERNATE_MATERIAL_FIELD);
  }, [register]);

  return (
    <>
      <SettingsLabel htmlFor="alternativeMaterialName">
        Alternative Material
      </SettingsLabel>
      <Styled.LessonMaterialsRow $marginBottom={8}>
        <div>
          <Styled.LessonMaterialsAutoSuggest
            $itemClassName="item"
            data-selector="activity-material-alternate-item"
          >
            <AutoSuggestWidget<
              Pluck<SearchMaterialsQuery, 'searchMaterials'>,
              Pluck<SearchMaterialsQuery, 'searchMaterials'>
            >
              suggestions={filteredList}
              theme={defaultTheme}
              getSuggestionValue={suggestion => suggestion.name}
              inputProps={{
                id: ALTERNATE_MATERIAL_FIELD,
                name: ALTERNATE_MATERIAL_FIELD,
                type: 'search',
                autoComplete: 'off',
                onChange: (_, params) => {
                  handleOnChange(params);
                },
                onKeyDown: event => {
                  if (event.key === ENTER_KEY) {
                    event.preventDefault();
                  }
                },
                value: materialName,
                placeholder: 'Material',
              }}
              onSuggestionsFetchRequested={event =>
                setMaterialName(event.value)
              }
              onSuggestionsClearRequested={() => {}}
              onSuggestionSelected={(_, data) => {
                setAlternativeMaterials([
                  ...alternativeMaterials,
                  data.suggestion,
                ]);
                setMaterialName('');
                setValue(ALTERNATE_MATERIAL_FIELD, [
                  ...alternativeMaterialIds,
                  data.suggestion.id,
                ]);
              }}
              renderSuggestion={suggestion => {
                return (
                  <div
                    key={suggestion.id}
                    data-selector="activity-alternative-material-suggestion-option"
                  >
                    {suggestion.name}
                  </div>
                );
              }}
              renderSuggestionsContainer={options => (
                <RenderSuggestionsContainer
                  {...options}
                  isLoading={isLoading}
                />
              )}
            />
          </Styled.LessonMaterialsAutoSuggest>
        </div>
        <DeleteButtonIcon
          aria-label="Delete all alternative materials"
          onClick={() => {
            setValue(ALTERNATE_MATERIAL_FIELD, null);
            setAlternativeMaterials([]);
            onRemoveAll();
          }}
          data-selector="delete-alternative-materials-button"
        />
      </Styled.LessonMaterialsRow>
      <ValidationMessage
        noMarginTop
        id={ALTERNATE_MATERIAL_FIELD}
        field={ALTERNATE_MATERIAL_FIELD}
        errors={errors}
      />
      <Box $gap={8}>
        {alternativeMaterialIds.map(materialId => {
          const selectedMaterial = alternativeMaterials.find(
            am => am.id === materialId
          );
          return selectedMaterial ? (
            <Styled.LessonMaterialsPill
              type="button"
              key={materialId}
              $isSmall
              $withIcon
              $borderRadius={40}
              aria-label={`Click to remove ${selectedMaterial.name}`}
              onClick={() => {
                const updatedMaterials = filterOutSelectedMaterials(
                  alternativeMaterials,
                  [materialId]
                );
                const updatedMaterialIds = updatedMaterials.map(
                  material => material.id
                );
                setValue(ALTERNATE_MATERIAL_FIELD, updatedMaterialIds);
                setAlternativeMaterials(updatedMaterials);
              }}
              data-selector="remove-alternative-material"
            >
              {selectedMaterial.name}
              <IconClose width={12} height={12} />
            </Styled.LessonMaterialsPill>
          ) : null;
        })}
      </Box>
    </>
  );
};

const LessonMaterialEditCard = ({
  material,
  onEdit,
  onDeleteItem,
  onSave,
  isLegacyMaterial,
  existingMaterials: materials,
}: BaseProps & {
  onEdit: (material: MaterialItem) => void;
  isLegacyMaterial: boolean;
}) => {
  return !material.isEditMode ? (
    <Styled.LessonMaterialsItem data-selector="activity-material-card-container">
      <LessonMaterialsList list={[material]} />
      <SecondaryButton type="button" onClick={() => onEdit(material)}>
        Edit
      </SecondaryButton>
    </Styled.LessonMaterialsItem>
  ) : (
    <LessonMaterialsItemEdit
      material={material}
      onSave={onSave}
      onDeleteItem={onDeleteItem}
      isLegacyMaterial={isLegacyMaterial}
      existingMaterials={materials}
    />
  );
};

export default LessonMaterialEditCard;
